import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Alert, Autocomplete, Box, Button, ButtonBase, Chip, LinearProgress, Paper, Stack,
  Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow,
  TextField, Typography
} from "@mui/material";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import EditNoteIcon from "@mui/icons-material/EditNote";
import LocationOffIcon from "@mui/icons-material/LocationOff";
import PlaceIcon from "@mui/icons-material/Place";
import TaskAltIcon from "@mui/icons-material/TaskAlt";
import Application from "../../components/application";
import { AuthContext } from "../../contexts/auth";
import TELAS from "../../constants/telas";
import TELA_ACOES from "../../constants/telaAcoes";
import permissaoTelas from "../../utils/permissaoTelas";
import useDashboardTarefas from "../../hooks/useDashboardTarefas";

const PAGINA = {
  aplicacao: "SisPalo - Tarefas",
  funcionalidade: "Tarefas",
  tela: TELAS.TAREFAS,
  acoes: [TELA_ACOES.Consultar],
};

const CARDS_VALIDOS = [
  "previstos", "visitados", "pendentes", "finalizadas", "finalizadas_manuais",
];

// Converte Date para o formato AAAA-MM-DD aceito pelo endpoint.
const dataInput = (data) => [
  data.getFullYear(),
  String(data.getMonth() + 1).padStart(2, "0"),
  String(data.getDate()).padStart(2, "0"),
].join("-");

// O usuário escolhe um único mês. Estas funções geram o primeiro e o último
// dia real desse mês, inclusive fevereiro em anos bissextos.
const inicioDoMes = (valor) => valor ? `${String(valor).slice(0, 7)}-01` : '';
const fimDoMes = (valor) => {
  if (!valor) return '';
  const [ano, mes] = String(valor).slice(0, 7).split('-').map(Number);
  const ultimoDia = new Date(ano, mes, 0).getDate();
  return `${ano}-${String(mes).padStart(2, '0')}-${String(ultimoDia).padStart(2, '0')}`;
};

const lerFiltrosSalvos = (chave, padrao) => {
  // Filtros antigos com intervalo de datas são normalizados para o mês inicial.
  try {
    const filtros = JSON.parse(localStorage.getItem(chave));
    return filtros?.inicio
      ? {
        inicio: inicioDoMes(filtros.inicio),
        fim: fimDoMes(filtros.inicio),
        tipotarefa: String(filtros.tipotarefa || ""),
        posto: String(filtros.posto || ""),
        detalhe: CARDS_VALIDOS.includes(filtros.detalhe) ? filtros.detalhe : "pendentes",
      }
      : padrao;
  } catch {
    return padrao;
  }
};

const Indicador = ({ titulo, valor, legenda, cor, ativo, onClick, children }) => (
  <ButtonBase onClick={onClick} aria-pressed={ativo} sx={{ width: "100%", borderRadius: 2.5, textAlign: "left" }}>
    <Paper variant="outlined" sx={{
      width: "100%", height: "100%", p: 1.75, borderRadius: 2.5,
      borderColor: ativo ? cor : `${cor}35`, borderWidth: ativo ? 2 : 1,
      bgcolor: ativo ? `${cor}0c` : "background.paper", borderTop: `3px solid ${cor}`,
      boxShadow: ativo ? `0 8px 20px ${cor}28` : `0 6px 18px ${cor}16`,
      transition: "transform .2s ease, box-shadow .2s ease",
      "&:hover": { transform: "translateY(-3px)", boxShadow: `0 10px 24px ${cor}28` },
    }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center" gap={1}>
        <Box>
          <Typography variant="caption" color="text.secondary">{titulo}</Typography>
          <Typography variant="h4" fontWeight={800} sx={{ color: cor, lineHeight: 1.2 }}>{valor ?? 0}</Typography>
          <Typography variant="caption" color="text.secondary">{legenda}</Typography>
        </Box>
        <Box sx={{ color: "white", bgcolor: cor, display: "flex", p: 1, borderRadius: 2, boxShadow: `0 5px 12px ${cor}45` }}>{children}</Box>
      </Stack>
    </Paper>
  </ButtonBase>
);

const DashboardTarefas = () => {
  // O mês corrente é usado apenas quando o usuário ainda não possui preferência.
  const hoje = useMemo(() => new Date(), []);
  const inicioMes = useMemo(() => new Date(hoje.getFullYear(), hoje.getMonth(), 1), [hoje]);
  const navigate = useNavigate();
  const { permissaoUsuario, user } = useContext(AuthContext);
  // O ID na chave impede que dois gestores do mesmo navegador compartilhem filtros.
  const chaveFiltros = `paloGestor@data@dashboard-tarefas@filters@${user.value?.id}`;
  const filtrosIniciais = useMemo(() => lerFiltrosSalvos(chaveFiltros, {
    inicio: dataInput(inicioMes),
    fim: fimDoMes(dataInput(inicioMes)),
    tipotarefa: "",
    posto: "",
    detalhe: "pendentes",
  }), [chaveFiltros, hoje, inicioMes]);
  const [inicio, setInicio] = useState(filtrosIniciais.inicio);
  const [fim, setFim] = useState(filtrosIniciais.fim);
  const [tipoTarefa, setTipoTarefa] = useState(filtrosIniciais.tipotarefa);
  const [posto, setPosto] = useState(filtrosIniciais.posto);
  // Mantém separados os controles em edição e os filtros da consulta exibida.
  // Assim, trocar um campo sem clicar em Consultar não altera a paginação atual.
  const [filtrosAplicados, setFiltrosAplicados] = useState(filtrosIniciais);
  const [cardSelecionado, setCardSelecionado] = useState(filtrosIniciais.detalhe);
  const [paginaDetalhes, setPaginaDetalhes] = useState(0);
  const [linhasPorPagina, setLinhasPorPagina] = useState(10);
  // Evita selecionar novamente o primeiro item caso o usuário limpe o campo.
  const selecaoInicialAplicada = useRef(false);
  const { dados, loading, erro, carregar } = useDashboardTarefas();

  useEffect(() => {
    // A rota usa a mesma permissão de consulta da tela de Tarefas.
    if (!permissaoTelas(permissaoUsuario, TELA_ACOES.Consultar, PAGINA)) {
      navigate("/gestor/dashboard");
      return;
    }
    // A primeira consulta restaura automaticamente a preferência deste usuário.
    carregar({
      inicio: filtrosIniciais.inicio,
      fim: filtrosIniciais.fim,
      tipotarefa: filtrosIniciais.tipotarefa || undefined,
      posto: filtrosIniciais.posto || undefined,
      detalhe: filtrosIniciais.detalhe,
      page: 1,
      page_size: 10,
    });
  }, [carregar, filtrosIniciais, navigate, permissaoUsuario]);

  const consultar = () => {
    // Salva somente filtros funcionais; página e quantidade de linhas são temporárias.
    const filtros = { inicio, fim, tipotarefa: tipoTarefa, posto, detalhe: cardSelecionado };
    localStorage.setItem(chaveFiltros, JSON.stringify(filtros));
    setFiltrosAplicados(filtros);
    setPaginaDetalhes(0);
    carregar({ ...filtros, tipotarefa: tipoTarefa || undefined, detalhe: cardSelecionado, page: 1, page_size: linhasPorPagina });
  };
  const resumo = dados?.resumo || {};
  const cobertura = Number(resumo.percentual_visitado || 0);
  const tipos = dados?.tipos_tarefa || [];
  // O endpoint retorna somente tipos editáveis realmente cadastrados.
  const opcoesTipos = dados?.opcoes_tipos_tarefa || tipos;
  const tipoSelecionado = opcoesTipos.find((item) => String(item.id) === tipoTarefa) || null;
  const opcoesPostos = dados?.opcoes_postos || [];
  const postoSelecionado = opcoesPostos.find((item) => String(item.id) === posto) || null;
  const detalhes = dados?.detalhes || [];
  const paginacaoDetalhes = dados?.detalhes_paginacao || {};

  useEffect(() => {
    if (selecaoInicialAplicada.current || !opcoesTipos.length) return;

    const tipoSalvoAindaExiste = tipoTarefa
      && opcoesTipos.some((item) => String(item.id) === tipoTarefa);
    if (tipoSalvoAindaExiste) {
      selecaoInicialAplicada.current = true;
      return;
    }

    // Na primeira abertura sem preferência, seleciona e consulta o primeiro tipo.
    const primeiroTipo = String(opcoesTipos[0].id);
    const filtros = { inicio, fim, tipotarefa: primeiroTipo, posto: "", detalhe: cardSelecionado };
    selecaoInicialAplicada.current = true;
    setTipoTarefa(primeiroTipo);
    setPosto("");
    setFiltrosAplicados(filtros);
    setPaginaDetalhes(0);
    localStorage.setItem(chaveFiltros, JSON.stringify(filtros));
    carregar({ ...filtros, detalhe: cardSelecionado, page: 1, page_size: linhasPorPagina });
  }, [cardSelecionado, carregar, chaveFiltros, fim, inicio, linhasPorPagina, opcoesTipos, tipoTarefa]);

  // Pagina apenas o grid selecionado; os cards permanecem com os totais gerais.
  const carregarDetalhes = (novaPagina, novoTamanho = linhasPorPagina, novoCard = cardSelecionado) => {
    setPaginaDetalhes(novaPagina);
    carregar({
      inicio: filtrosAplicados.inicio,
      fim: filtrosAplicados.fim,
      tipotarefa: filtrosAplicados.tipotarefa || undefined,
      posto: filtrosAplicados.posto || undefined,
      detalhe: novoCard,
      page: novaPagina + 1,
      page_size: novoTamanho,
    });
  };

  const selecionarCard = (card) => {
    setCardSelecionado(card);
    // O último indicador também é uma preferência individual do gestor.
    localStorage.setItem(chaveFiltros, JSON.stringify({
      ...filtrosAplicados,
      detalhe: card,
    }));
    carregarDetalhes(0, linhasPorPagina, card);
  };

  // Abre o detalhe preservando a origem para o botão Voltar retornar ao dashboard.
  const abrirDetalheTarefa = (tarefaId) => {
    if (tarefaId) {
      navigate(`/gestor/tarefas/formulario/${tarefaId}?origem=dashboard-tarefas`);
    }
  };

  // Define títulos e colunas do grid conforme o indicador selecionado.
  const configuracoesGrid = {
    previstos: { titulo: "Locais previstos", descricao: "Locais e tipos de tarefa esperados para o filtro.", tarefas: false, cor: "#56627a", fundo: "#f1f3f6" },
    visitados: { titulo: "Locais visitados", descricao: "Última tarefa concluída de cada local e tipo.", tarefas: true, cor: "#36855a", fundo: "#edf8f1" },
    pendentes: { titulo: "Locais ainda não visitados", descricao: "Pendências conforme o tipo de tarefa selecionado.", tarefas: false, cor: "#b84b4b", fundo: "#f8eeee" },
    finalizadas: { titulo: "Tarefas finalizadas", descricao: "Finalizações normais realizadas no mês.", tarefas: true, cor: "#3f6694", fundo: "#eef3f8" },
    finalizadas_manuais: { titulo: "Tarefas finalizadas manualmente", descricao: "Finalizações feitas pelo gestor com observação.", tarefas: true, observacao: true, cor: "#9a642f", fundo: "#f8f2ec" },
  };
  const gridAtual = configuracoesGrid[cardSelecionado];
  const totalColunas = gridAtual.tarefas ? (gridAtual.observacao ? 7 : 6) : 3;

  return (
    <Application>
      <Box sx={{ p: { xs: 1, sm: 2 }, maxWidth: 1500, mx: "auto", minHeight: "calc(100vh - 70px)", bgcolor: "#f4f5f7" }}>
        <Stack spacing={2}>
          <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ sm: "center" }} gap={1}>
            <Box>
              <Typography variant="h5" fontWeight={800}>Dashboard de tarefas</Typography>
              <Typography variant="body2" color="text.secondary">Acompanhe as visitas aos locais por período e tipo de tarefa.</Typography>
            </Box>
            <Button color="warning" variant="contained" onClick={() => navigate("/gestor/dashboard")}>Voltar</Button>
          </Stack>

          {/* Filtros enviados ao endpoint agregado do dashboard. */}
          <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2.5, borderColor: "#c7d7f4", boxShadow: "0 5px 16px rgba(51,79,134,.08)" }}>
            <Stack direction={{ xs: "column", md: "row" }} spacing={1.25} alignItems={{ md: "center" }}>
              <TextField
                label="Mês de referência"
                type="month"
                size="small"
                value={inicio.slice(0, 7)}
                onChange={(e) => {
                  setInicio(inicioDoMes(e.target.value));
                  setFim(fimDoMes(e.target.value));
                }}
                InputLabelProps={{ shrink: true }}
              />
              {/* Exibe somente tipos editáveis cadastrados e permite pesquisa por nome. */}
              <Autocomplete
                size="small"
                options={opcoesTipos}
                value={tipoSelecionado}
                onChange={(_, novoTipo) => {
                  setTipoTarefa(novoTipo ? String(novoTipo.id) : "");
                  setPosto("");
                }}
                getOptionLabel={(item) => item.nome || ""}
                isOptionEqualToValue={(opcao, valor) => String(opcao.id) === String(valor.id)}
                noOptionsText="Nenhum tipo de tarefa encontrado"
                clearText="Limpar"
                openText="Abrir"
                closeText="Fechar"
                sx={{ width: { xs: "100%", md: 280 } }}
                renderInput={(params) => (
                  <TextField {...params} label="Pesquisar tipo de tarefa" placeholder="Digite o nome" />
                )}
              />
              {/* Postos podem ser numerosos, por isso a seleção usa autocomplete. */}
              <Autocomplete
                size="small"
                options={opcoesPostos}
                value={postoSelecionado}
                onChange={(_, novoPosto) => setPosto(novoPosto ? String(novoPosto.id) : "")}
                getOptionLabel={(item) => `${item.codigo} - ${item.nome}`}
                isOptionEqualToValue={(opcao, valor) => String(opcao.id) === String(valor.id)}
                noOptionsText="Nenhum posto encontrado"
                clearText="Limpar"
                openText="Abrir"
                closeText="Fechar"
                sx={{ width: { xs: "100%", md: 320 } }}
                renderInput={(params) => (
                  <TextField {...params} label="Pesquisar posto" placeholder="Digite o código ou nome" />
                )}
              />
              <Button variant="contained" onClick={consultar} disabled={loading || !inicio || !fim}>Consultar</Button>
            </Stack>
          </Paper>

          {loading && <LinearProgress />}
          {erro && <Alert severity="error">{erro}</Alert>}

          {!erro && dados && <>
            {/* Indicadores gerais calculados no servidor sobre todos os registros. */}
            <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", sm: "repeat(2, 1fr)", lg: "repeat(5, 1fr)" }, gap: 1.25 }}>
              <Indicador titulo="Locais previstos" valor={resumo.total_locais} legenda="para os filtros selecionados" cor="#56627a" ativo={cardSelecionado === "previstos"} onClick={() => selecionarCard("previstos")}><PlaceIcon fontSize="large" /></Indicador>
              <Indicador titulo="Locais visitados" valor={resumo.locais_visitados} legenda="com tarefa finalizada" cor="#36855a" ativo={cardSelecionado === "visitados"} onClick={() => selecionarCard("visitados")}><TaskAltIcon fontSize="large" /></Indicador>
              <Indicador titulo="Ainda não visitados" valor={resumo.locais_nao_visitados} legenda="locais pendentes" cor="#b84b4b" ativo={cardSelecionado === "pendentes"} onClick={() => selecionarCard("pendentes")}><LocationOffIcon fontSize="large" /></Indicador>
              <Indicador titulo="Tarefas finalizadas" valor={resumo.tarefas_finalizadas} legenda={`${resumo.tarefas_em_andamento || 0} em andamento • inclui Check-ins`} cor="#3f6694" ativo={cardSelecionado === "finalizadas"} onClick={() => selecionarCard("finalizadas")}><AssignmentTurnedInIcon fontSize="large" /></Indicador>
              <Indicador titulo="Finalizadas manualmente" valor={resumo.tarefas_finalizadas_manuais} legenda="finalizadas pelo gestor com observação" cor="#9a642f" ativo={cardSelecionado === "finalizadas_manuais"} onClick={() => selecionarCard("finalizadas_manuais")}><EditNoteIcon fontSize="large" /></Indicador>
            </Box>

            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2.5, borderColor: "#ccd5cf", bgcolor: "background.paper", boxShadow: "0 3px 10px rgba(0,0,0,.05)" }}>
              <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
                <Box><Typography fontWeight={700}>Visitas aos locais</Typography><Typography variant="caption" color="text.secondary">Percentual de locais visitados no período</Typography></Box>
                <Typography variant="h5" fontWeight={800} color="primary.main">{cobertura.toFixed(1)}%</Typography>
              </Stack>
              <LinearProgress variant="determinate" value={Math.min(100, cobertura)} color={cobertura >= 70 ? "success" : cobertura >= 40 ? "warning" : "error"} sx={{ height: 10, borderRadius: 5 }} />
            </Paper>

            {/* Um local aparece uma vez para cada tipo de tarefa vinculado. */}
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", borderColor: "#c8d7f2", boxShadow: "0 5px 16px rgba(40,120,208,.07)" }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: "#f1f3f6", borderLeft: "4px solid #3f6694" }}><Typography fontWeight={700} color="text.primary">Visitas por tipo de tarefa</Typography><Typography variant="caption" color="text.secondary">Um local pode estar visitado em um tipo e pendente em outro.</Typography></Box>
              <TableContainer><Table size="small">
                <TableHead sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: "#e8f0fb",
                    color: "#294f7a",
                    fontWeight: 800,
                    fontSize: ".86rem",
                    borderBottom: "1px solid #bfd0e8",
                  },
                }}><TableRow><TableCell>Tipo de tarefa</TableCell><TableCell align="right">Previstos</TableCell><TableCell align="right">Visitados</TableCell><TableCell align="right">Pendentes</TableCell><TableCell align="right">Visitas</TableCell></TableRow></TableHead>
                <TableBody>
                  {tipos.map((tipo) => <TableRow key={tipo.id} hover sx={{ "& td": { py: 1.25 } }}><TableCell sx={{ fontWeight: 700, fontSize: ".92rem" }}>{tipo.nome}</TableCell><TableCell align="right" sx={{ fontWeight: 700, fontSize: "1rem" }}>{tipo.total_locais}</TableCell><TableCell align="right" sx={{ color: "#237a45", fontWeight: 800, fontSize: "1rem" }}>{tipo.locais_visitados}</TableCell><TableCell align="right" sx={{ color: "#bd3434", fontWeight: 800, fontSize: "1rem" }}>{tipo.locais_nao_visitados}</TableCell><TableCell align="right"><Chip label={`${Number(tipo.percentual_visitado || 0).toFixed(1)}%`} color={tipo.percentual_visitado >= 70 ? "success" : tipo.percentual_visitado >= 40 ? "warning" : "error"} sx={{ height: 28, fontWeight: 800, fontSize: ".85rem" }} /></TableCell></TableRow>)}
                  {tipos.length === 0 && <TableRow><TableCell colSpan={5} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhum tipo de tarefa disponível.</TableCell></TableRow>}
                </TableBody>
              </Table></TableContainer>
            </Paper>

            {/* O conteúdo e as colunas mudam conforme o card selecionado. */}
            <Paper variant="outlined" sx={{ borderRadius: 2.5, overflow: "hidden", borderColor: `${gridAtual.cor}55`, boxShadow: `0 5px 16px ${gridAtual.cor}14` }}>
              <Box sx={{ px: 2, py: 1.5, bgcolor: gridAtual.fundo, borderLeft: `4px solid ${gridAtual.cor}` }}><Typography fontWeight={700} color="text.primary">{gridAtual.titulo}</Typography><Typography variant="caption" color="text.secondary">{gridAtual.descricao}</Typography></Box>
              <TableContainer sx={{ maxHeight: 360 }}><Table stickyHeader size="small">
                <TableHead sx={{
                  "& .MuiTableCell-head": {
                    bgcolor: gridAtual.fundo,
                    color: gridAtual.cor,
                    fontWeight: 700,
                    borderBottom: `1px solid ${gridAtual.cor}45`,
                  },
                }}><TableRow><TableCell>Local</TableCell><TableCell>Posto</TableCell><TableCell>Tipo de tarefa</TableCell>{gridAtual.tarefas && <><TableCell>Usuário</TableCell><TableCell>Entrada</TableCell><TableCell>Saída</TableCell></>}{gridAtual.observacao && <TableCell>Observação</TableCell>}</TableRow></TableHead>
                <TableBody>
                  {detalhes.map((item, index) => <TableRow
                    key={`${item.id || item.local_id}-${index}`}
                    hover
                    tabIndex={item.tarefa_id ? 0 : undefined}
                    title={item.tarefa_id ? "Clique para abrir os detalhes da tarefa" : undefined}
                    onClick={() => abrirDetalheTarefa(item.tarefa_id)}
                    onKeyDown={(event) => {
                      if (item.tarefa_id && (event.key === "Enter" || event.key === " ")) {
                        event.preventDefault();
                        abrirDetalheTarefa(item.tarefa_id);
                      }
                    }}
                    sx={{ cursor: item.tarefa_id ? "pointer" : "default" }}
                  ><TableCell sx={{ fontWeight: 600 }}>{item.local}</TableCell><TableCell>{item.posto}</TableCell><TableCell>{String(item.tipotarefa || "").trim() || "Check-in"}</TableCell>{gridAtual.tarefas && <><TableCell>{item.usuario || "-"}</TableCell><TableCell>{item.entrada || "-"}</TableCell><TableCell>{item.saida || "-"}</TableCell></>}{gridAtual.observacao && <TableCell sx={{ whiteSpace: "pre-line", minWidth: 260 }}>{item.observacao || "-"}</TableCell>}</TableRow>)}
                  {detalhes.length === 0 && <TableRow><TableCell colSpan={totalColunas} align="center" sx={{ py: 3, color: "text.secondary" }}>Nenhum registro encontrado para o indicador selecionado.</TableCell></TableRow>}
                </TableBody>
              </Table></TableContainer>
              <TablePagination
                component="div"
                count={paginacaoDetalhes.count || 0}
                page={paginaDetalhes}
                onPageChange={(_, novaPagina) => carregarDetalhes(novaPagina)}
                rowsPerPage={linhasPorPagina}
                onRowsPerPageChange={(event) => {
                  const novoTamanho = Number(event.target.value);
                  setLinhasPorPagina(novoTamanho);
                  carregarDetalhes(0, novoTamanho);
                }}
                rowsPerPageOptions={[10, 20, 50, 100]}
                labelRowsPerPage="Linhas por página:"
                labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
              />
            </Paper>
          </>}
        </Stack>
      </Box>
    </Application>
  );
};

export default DashboardTarefas;
